import type { PartialFailureInfo } from '@core/utils/partialFailureUtils';
import { subscribeToPartialFailures } from '@core/utils/partialFailureUtils';
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import * as styles from './NotificationProvider.css.ts';

// Define the Notification type locally
export interface Notification {
  id: string;
  message: string;
  type: 'success' | 'warning' | 'error' | 'info';
  autoClose?: boolean;
  duration?: number;
}

// Simple NotificationContainer component
interface NotificationContainerProps {
  notifications: Notification[];
  onClose?: (id: string) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

function NotificationContainer({ notifications, onClose, position = 'top-right' }: NotificationContainerProps) {
  if (notifications.length === 0) {
    return null;
  }

  // Get position-specific class
  let positionClass = styles.topRight;

  switch (position) {
    case 'top-left':
      positionClass = styles.topLeft;
      break;
    case 'bottom-right':
      positionClass = styles.bottomRight;
      break;
    case 'bottom-left':
      positionClass = styles.bottomLeft;
      break;
    default:
      positionClass = styles.topRight;
  }

  return (
    <div className={`${styles.notificationContainer} ${positionClass}`}>
      {notifications.map(notification => {
        // Get type-specific label class
        let labelClass = styles.infoLabel;

        switch (notification.type) {
          case 'success':
            labelClass = styles.successLabel;
            break;
          case 'warning':
            labelClass = styles.warningLabel;
            break;
          case 'error':
            labelClass = styles.errorLabel;
            break;
          default:
            labelClass = styles.infoLabel;
        }

        return (
          <div key={notification.id} className={styles.notification}>
            <div className={styles.notificationHeader}>
              <span className={`${styles.notificationTypeLabel} ${labelClass}`}>{notification.type.toUpperCase()}</span>
              <button aria-label="Close notification" className={styles.closeButton} onClick={() => onClose?.(notification.id)}>
                ×
              </button>
            </div>
            <div className={styles.notificationMessage}>{notification.message}</div>
          </div>
        );
      })}
    </div>
  );
}

// Define the context type
interface NotificationContextType {
  addNotification: (notification: Omit<Notification, 'id'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

// Create the context with a default value
const NotificationContext = createContext<NotificationContextType>({
  addNotification: () => '',
  removeNotification: () => {},
  clearNotifications: () => {},
});

// Hook to use the notification context
export const useNotification = () => useContext(NotificationContext);

interface NotificationProviderProps {
  children: React.ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

export function NotificationProvider({ children, position = 'top-right' }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Add a new notification
  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = uuidv4();
    const newNotification = {
      ...notification,
      id,
      autoClose: notification.autoClose ?? true,
      duration: notification.duration ?? 5000,
    };

    setNotifications(prev => [...prev, newNotification]);

    return id;
  }, []);

  // Remove a notification by ID
  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Helper function to get a user-friendly message for a partial failure
  const getPartialFailureMessage = useCallback((failure: PartialFailureInfo): string => {
    // Extract the field name from the path if available
    let fieldName = '';

    if (failure.path && failure.path.length > 0) {
      const lastPathSegment = failure.path[failure.path.length - 1];

      fieldName = typeof lastPathSegment === 'string' ? lastPathSegment : '';
    }

    // Create a user-friendly message based on the field name
    if (fieldName === 'generatedPrompts') {
      return `Community created successfully, but prompt generation failed: ${failure.message}`;
    }

    return failure.message;
  }, []);

  // Handle partial failures
  const handlePartialFailures = useCallback(
    (failures: PartialFailureInfo[]) => {
      failures.forEach(failure => {
        const message = getPartialFailureMessage(failure);

        // Only add notifications for new failures
        const existingNotification = notifications.find(n => n.message === message);

        if (!existingNotification) {
          addNotification({
            type: 'warning',
            message,
            autoClose: true,
            duration: 8000,
          });
        }
      });
    },
    [addNotification, getPartialFailureMessage, notifications]
  );

  // Set up auto-close timers for notifications
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    notifications.forEach(notification => {
      if (notification.autoClose && notification.duration) {
        const timer = setTimeout(() => {
          removeNotification(notification.id);
        }, notification.duration);

        timers.push(timer);
      }
    });

    // Clean up timers
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [notifications, removeNotification]);

  // Subscribe to partial failures
  useEffect(() => {
    const unsubscribe = subscribeToPartialFailures(handlePartialFailures);

    return () => {
      unsubscribe();
    };
  }, [handlePartialFailures]);

  return (
    <NotificationContext.Provider
      value={{
        addNotification,
        removeNotification,
        clearNotifications,
      }}
    >
      {children}
      <NotificationContainer notifications={notifications} position={position} onClose={removeNotification} />
    </NotificationContext.Provider>
  );
}
